import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
  RefAttributes,
} from "react";
import styles from "./Button.module.css";

export type ButtonVariant = "primary" | "secondary" | "quiet";
export type ButtonSize = "md" | "lg";

/*
 * `| undefined` is spelled out on every optional prop because tsconfig sets
 * exactOptionalPropertyTypes: passing an explicitly undefined className (which is what a
 * CSS-module lookup yields) would otherwise be a type error at every call site.
 */
interface CommonProps {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  children: ReactNode;
  className?: string | undefined;
}

function classesFor({ variant = "primary", size = "md", className }: CommonProps) {
  return [styles.button, styles[variant], size === "lg" ? styles.lg : null, className]
    .filter(Boolean)
    .join(" ");
}

/**
 * `ref` is declared because React 19 passes it as an ordinary prop to a function component.
 * The seller desk uses it to return focus to the control a founder left — the button that
 * opened an editor — rather than dropping them at the top of the document when it closes.
 */
export function Button({
  variant,
  size,
  className,
  children,
  type = "button",
  ...rest
}: CommonProps & ButtonHTMLAttributes<HTMLButtonElement> & RefAttributes<HTMLButtonElement>) {
  return (
    <button
      type={type}
      className={classesFor({ variant, size, className, children })}
      {...rest}
    >
      {children}
    </button>
  );
}

/**
 * A link styled as a button. Navigation stays an anchor so it keeps middle-click, copy
 * link and browser history behaviour.
 */
export function ButtonLink({
  variant,
  size,
  className,
  children,
  ...rest
}: CommonProps & AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a className={classesFor({ variant, size, className, children })} {...rest}>
      {children}
    </a>
  );
}
